/** @jsxImportSource @emotion/react */
import React  from 'react';
import { css, jsx } from '@emotion/react';
import Table, { Cell, Column, AvatarCell, VisibleOnRowHover } from 'components/table';
import { FormattedMessage, FormattedNumber, FormattedDate } from 'components/i18n';
import {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import { Link } from 'react-router-dom';
import Tag from '@atlaskit/tag';
import { Customer, RecurringSale, Sale } from '../types';
import { CompanyAvatar } from '../../../components/avatar';
import { MoreDropdownMenu } from '../../../components/dropdown-menu';
import { TableProps } from '../../../components/table';
import RecurringSaleStatus from './RecurringSaleStatus';
import RecurringSaleSchedule from './RecurringSaleSchedule';

interface Props extends Omit<TableProps<RecurringSale>, 'columns'> {
}

const MoreDropdownCell = React.memo(() => {
  const dropDownItemGroup = React.useMemo(()=> (
    <DropdownItemGroup>
      <DropdownItem><FormattedMessage id="sale.RecurringInvoicesTable.options.view" defaultMessage="View" /></DropdownItem>
      <DropdownItem><FormattedMessage id="sale.RecurringInvoicesTable.options.remove" defaultMessage="Remove" /></DropdownItem>
    </DropdownItemGroup>
  )
  , []);
  return (
    <VisibleOnRowHover>
      <MoreDropdownMenu>
        {dropDownItemGroup}
      </MoreDropdownMenu>
    </VisibleOnRowHover>
  );
});

export const CustomerCell = React.memo(function CustomerCell({customer}: {customer?: Customer}) {
  const avatar = React.useMemo(()=><CompanyAvatar size="medium" company={customer!} />, [customer]);
  return (
    <AvatarCell
      avatar={avatar}
      primaryText={customer?.name}
      secondaryText={customer?.contactName}
    />
  );
});

const columns: Column<RecurringSale>[] =
  [
    // {
    //   Header: <FormattedMessage id="sale.RecurringInvoicesTable.headings.startDate" defaultMessage="Start date"/>,
    //   accessor: 'startDate',
    //   width: 100,
    //   Cell: ({value}) => value? <FormattedDate value={value}/>: <span/>,
    // },
    {
      Header: <FormattedMessage id="sale.RecurringInvoicesTable.headings.customer" defaultMessage="Customer" />,
      accessor: 'customer',
      width: 240,
      disableSortBy: true,
      Cell: ({row: {original: { customer}}}) => <CustomerCell customer={customer} />,
    },
    {
      Header: <FormattedMessage id="sale.RecurringInvoicesTable.headings.schedule" defaultMessage="Schedule" />,
      accessor: 'intervalUnit',
      width: 100,
      Cell: ({value, row: {original: recurringSale}}) => <RecurringSaleSchedule intervalUnit={recurringSale.intervalUnit} intervalNumber={recurringSale.intervalNumber} />
    },
    {
      Header: <FormattedMessage id="sale.RecurringInvoicesTable.headings.lastInvoice" defaultMessage="Last invoice" />,
      accessor: 'lastInvoiceDate',
      width: 100,
      Cell: ({value}) => value? <FormattedDate value={value} />: <span />,
    },
    {
      Header: <FormattedMessage id="sale.RecurringInvoicesTable.headings.nextInvoice" defaultMessage="Next invoice" />,
      accessor: 'nextInvoiceDate',
      width: 100,
      Cell: ({value}) => value? <FormattedDate value={value} /> : <span />,
    },
    {
      Header: <FormattedMessage id="sale.RecurringInvoicesTable.headings.totalAmount" defaultMessage="Total amount" />,
      align: 'right',
      accessor: 'totalAmount',
      width: 100,
      Cell: ({value}) => <FormattedNumber value={value!} />,
    },
    {
      Header: <FormattedMessage id="sale.RecurringInvoicesTable.headings.status" defaultMessage="Status" />,
      accessor: 'isActive',
      Cell: React.memo(({value}) => <RecurringSaleStatus isActive={value} />),
      width: 100,
    },
    // {
    //   id: 'actions',
    //   Header: "",
    //   minWidth: 56,
    //   width: 56,
    //   align: 'right',
    //   Cell: () => <MoreDropdownCell/>
    // },
  ];

const RecurringInvoicesTable = (props: Props) => {
  const tableColumns = React.useMemo(() => columns, [columns]);

  return (
    <Table
      name="RecurringInvoicesTable"
      skeletonCount={12}
      columns={tableColumns}
      manualSortBy
      hasPagination
      {...props}
      getRowTo={(recurringSale)=> recurringSale.id}

    />
  );
};

RecurringInvoicesTable.defaultProps = {
  sales: [],
};

export default React.memo(RecurringInvoicesTable);
